<!--
  @fileoverview Quick Replies Component - Suggestion Chip Buttons (Vue 3)

  Renders quick reply buttons (suggestion chips) that users can tap
  to send predefined responses. Quick replies appear below bot messages
  and provide a fast way to respond without typing.

  @component MessageQuickReplies
  @displayName Quick Reply Buttons

  Features:
  - Horizontal wrapping layout
  - Pill-shaped buttons (rounded corners)
  - Active/inactive state styling
  - Disabled state during live chat (configurable)
  - UI Beta mode with different styling

  @example
  <MessageQuickReplies
    :message="messageWithQuickReplies"
    :button-outline="'primary'"
    :should-disable-if-in-livechat="false"
    @sendPostback="handleQuickReply"
  />

  @vue3migration
  - Migrated to script setup syntax
  - Replaced $store.getters with useMainStore()
  - Removed .native modifier from @click (not needed in Vue 3)
  - Replaced $forceUpdate with proper reactive state management
  - Updated Element UI to Element Plus (el-row -> ElRow, el-button -> ElButton)
  - Added proper TypeScript-style prop definitions
-->
<template>
  <!-- Flexbox row with wrapping for quick reply buttons -->
  <ElRow class="mx-4 mb-4 mt-0 flex flex-row flex-wrap">
    <!-- Quick reply button for each reply option -->
    <ElButton
      v-for="(reply, qrindex) in message.data.quickReplies"
      :key="qrindex"
      target="_blank"
      :disabled="isDisabledButton(reply)"
      :type="buttonOutline"
      :plain="!reply.active"
      :class="{ 'blue-button': buttonOutline === 'primary' }"
      size="small"
      @click.stop.prevent="sendPostback(reply)"
    >
      {{ reply.text }}
    </ElButton>
  </ElRow>
</template>

<script setup>
/**
 * @module components/ChatWindow/Message/QuickReplies
 * @description Quick reply suggestion buttons for fast user responses (Vue 3 Composition API)
 */
import { computed } from 'vue'
import { ElRow, ElButton } from 'element-plus'
import { useMainStore } from '@/stores/main'
import isDisabledButtonHelper from '@/helpers/isDisabledButton'

/**
 * Component props with proper types
 */
const props = defineProps({
  /**
   * Message object containing quick reply data
   */
  message: {
    type: Object,
    required: true,
  },
  /**
   * Button outline style/type
   */
  buttonOutline: {
    type: String,
    default: '',
  },
  /**
   * Whether to disable buttons when in live chat mode
   */
  shouldDisableIfInLivechat: {
    type: Boolean,
    default: false,
  },
})

/**
 * Component emits
 * @event sendPostback - Emits reply object to parent for processing
 */
const emit = defineEmits(['sendPostback'])

/**
 * Pinia store instance
 */
const store = useMainStore()

/**
 * Access webchat settings from Pinia store
 */
const settings = computed(() => store.settings)

/**
 * Handle quick reply button click
 *
 * Emits the postback event with reply data.
 *
 * @param {Object} reply - Quick reply object with text and payload
 * @emits sendPostback - Emits reply object to parent for processing
 */
function sendPostback(reply) {
  emit('sendPostback', reply)
}

/**
 * Check if button should be disabled
 *
 * Delegates to helper function that checks:
 * - If button has already been clicked (oneTimeUse)
 * - If live chat is active and buttons disabled during live chat
 *
 * @param {Object} button - Button object to check
 * @returns {boolean} True if button should be disabled
 */
function isDisabledButton(button) {
  // Create a context object that mimics the component instance
  // for backward compatibility with the helper function
  const context = {
    message: props.message,
    settings: settings.value,
    shouldDisableIfInLivechat: props.shouldDisableIfInLivechat,
    $store: {
      getters: {
        settings: settings.value,
        messages: store.messages,
        isSendingPostback: store.isSendingPostback,
      },
    },
  }
  return isDisabledButtonHelper(context, button)
}
</script>

<style lang="scss" scoped>
.el-button {
  margin: 0 5px 5px 0;
  // flex: 1 1 48%;
  border-radius: 25px;
  font-family: var(--keyreply-font-family);

  &.active {
    background: transparent;
  }
}
</style>

<style lang="scss" scoped>
.ui-beta {
  .el-button {
    margin: 0 5px 5px 0;
    border-radius: 8px;
    white-space: normal;
    text-align: left;

    &.blue-button {
      color: #4e6cce;
      background: #dce2f5;
      border-color: #4e6cce;

      &:hover {
        background: #4e6cce;
        color: #fff;
      }
    }

    &.active {
      background: transparent;
    }
  }
}
</style>
